import React, { Component } from 'react';
import { connect } from 'react-redux';

import { idCreditStatusChange } from '../../../../../state/Login';
import { NavHashLink } from 'react-router-hash-link';

import Loader from '../../../components/Loader';
import { privateIdCreditApplicationRequest } from '../../../../../Networking';
import IconBill from './assets/Icon_Bill_Darker_20px.svg';
import IconTagEuro from './assets/tag-euro_SVG.svg';
import IconCard from './assets/Icon_Card_Yellow-32px.svg';
import styles from './IdCreditApplicationPage.module.scss';

// One icon per "Mis see on?" tile, in the order the translations list them:
// monthly invoice, no cost, loyalty discounts.
const TILE_ICONS = [IconBill, IconTagEuro, IconCard];

// "Blocked" means a credit decision was made and it was a no — it must never stand in
// for "we haven't run the check yet". A client who has simply never been scored is
// invited to apply; the credit check runs as part of the application.
const phaseFor = (result) => {
    if (!result) {
        return 'eligible';
    }
    if (result.pending) {
        return 'pending';
    }
    const maxCredit = parseInt(result.maxCredit, 10) || 0;
    if (maxCredit > 0) {
        return 'eligible';
    }
    const hasDecision = !!(result.decisionDate || result.reference);
    return hasDecision ? 'blocked' : 'eligible';
};

const SUPPORT_PHONE = '6 100 100';
const SUPPORT_EMAIL = 'kaart@olerex.ee';
// react-router-hash-link jumps to the anchor by default; scroll it instead. The sections
// carry scroll-margin-top, which scrollIntoView honours, so they clear the sticky header.
const smoothScroll = (element) => element.scrollIntoView({ behavior: 'smooth', block: 'start' });

const SECTION_IDS = ['mis-see-on', 'taotlemine', 'tingimused', 'rohkem'];
// The FAQ and the general terms live on olerex.ee as two pages. They used to be copied
// into this page from the old portal, which meant two copies of a binding credit
// agreement that could drift apart.
const FAQ_URL = 'https://olerex.ee/idcredit/kkk';
const TERMS_URL = 'https://olerex.ee/idcredit/uldtingimused';

// Informational ID credit page shown to clients who don't have ID credit yet — the
// self-service equivalent of the idcredit.olerex.ee landing page. The application
// itself runs in IdCreditApplicationModal, which this page opens. The status lookup
// here is cheap and never scores anyone: the credit rating is requested when the
// client submits the application, as in the idcredit portal.
class IdCreditApplicationPage extends Component {
    state = {
        phase: 'loading', // loading | eligible | blocked | pending
        eligibility: null,
    };

    componentDidMount() {
        this.checkEligibility();
    }

    get userId() {
        return this.props.login && this.props.login.currentRole && this.props.login.currentRole.userId;
    }

    // Development-only state preview (?idCreditState=eligible|blocked|pending) so the
    // three states can be checked without arranging the matching rows in the database.
    get forcedPhase() {
        if (process.env.NODE_ENV !== 'development') {
            return null;
        }
        const forced = new URLSearchParams(window.location.search).get('idCreditState');
        return ['eligible', 'blocked', 'pending'].indexOf(forced) === -1 ? null : forced;
    }

    checkEligibility() {
        if (this.forcedPhase) {
            this.setState({ phase: this.forcedPhase, eligibility: { maxCredit: 500 } });
            return;
        }
        if (!this.userId) {
            this.setState({ phase: 'blocked' });
            return;
        }
        this.props.idCreditRequest(
            { requestType: 'checkEligibility', userId: this.userId, token: this.props.login.token },
            data => {
                const result = data && data.records && data.records.result;

                // The card goes live about a quarter of an hour after signing, and the
                // login response cannot know that yet. Telling the app about it here is
                // what moves the client on to their invoices without a fresh login - with
                // the account number, which the invoices page cannot load without.
                if (result && result.hasCredit && result.accnr) {
                    this.props.idCreditStatusChange(true, result.accnr);
                    return;
                }

                this.setState({ eligibility: result || null, phase: phaseFor(result) });
            },
            // A failed lookup says nothing about the client's creditworthiness, so keep
            // inviting them to apply; the application itself will surface the error.
            () => this.setState({ phase: 'eligible' })
        );
    }

    openApplication = () => {
        import('./IdCreditApplicationModal').then(module => {
            const IdCreditApplicationModal = module.default;
            this.props.onModalOpen(
                <IdCreditApplicationModal {...{
                    userData: this.props.userData,
                    onCloseButtonClick: this.props.onModalClose,
                    refreshData: () => window.location.reload(),
                }} />
            );
        });
    };

    // The decision date and reference only render when the backend actually knows them —
    // they identify a real credit decision, so they are never filled in with placeholders.
    renderBlockedMeta(t) {
        const { eligibility } = this.state;
        const decisionDate = eligibility && eligibility.decisionDate;
        const reference = eligibility && eligibility.reference;
        if (!decisionDate && !reference) {
            return null;
        }
        return (
            <div className={styles.ctaBlockedMeta}>
                {decisionDate && `${t.blockedDecisionDate} ${decisionDate}`}
                {decisionDate && reference && ' · '}
                {reference && `${t.blockedReference} ${reference}`}
            </div>
        );
    }

    renderCtaBar(t) {
        const { phase } = this.state;
        return (
            <div className={styles.ctaBar}>
                {phase === 'eligible' &&
                    <div className={styles.ctaText}>
                        <div className={styles.ctaTitle}>{t.ctaTitle}</div>
                        <div className={styles.ctaNote}>{t.ctaNote}</div>
                    </div>}
                {phase === 'blocked' &&
                    <div className={styles.ctaBlocked}>
                        <svg className={styles.ctaBlockedIcon} width="34" height="34" viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="#ffcd00" />
                            <path d="M12 6.6v7.2" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" />
                            <circle cx="12" cy="17.4" r="1.35" fill="#1a1a1a" />
                        </svg>
                        <div>
                            <div className={styles.ctaBlockedTitle}>{t.blockedTitle}</div>
                            <div className={styles.ctaBlockedBody}>{t.blockedBody}</div>
                            {this.renderBlockedMeta(t)}
                        </div>
                    </div>}
                <div className={styles.ctaActions}>
                    {phase === 'eligible' &&
                        <button className={styles.applyButton} onClick={this.openApplication}>{this.props.translation.applyButton}</button>}
                    {phase === 'blocked' &&
                        <a className={styles.secondaryButton} href={`mailto:${SUPPORT_EMAIL}`}>{t.contactSupport}</a>}
                    <a className={styles.faqLink} href={FAQ_URL} target="_blank" rel="noopener noreferrer">{t.ctaFaqLink} →</a>
                </div>
            </div>
        );
    }

    renderStatusBand(t) {
        const appliedAt = this.state.eligibility && this.state.eligibility.appliedAt;
        return (
            <section className={styles.statusBand}>
                <div className={styles.statusLabel}>{t.statusLabel}</div>
                <div className={styles.statusTitle}>{t.pendingTitle}</div>
                <div className={styles.statusBody}>
                    {t.pendingBody}
                    {appliedAt && ` ${t.pendingSubmitted} ${appliedAt}.`}
                </div>
            </section>
        );
    }

    render() {
        const t = this.props.translation.page;

        if (this.state.phase === 'loading') {
            return <div className={styles.page}><Loader /></div>;
        }

        return (
            <div className={styles.page}>
                <div className={styles.breadcrumb}>
                    <NavHashLink to="/">{t.breadcrumbHome}</NavHashLink>
                    <span>/</span>
                    <span className={styles.breadcrumbCurrent}>{t.breadcrumbCurrent}</span>
                </div>

                <section className={styles.hero}>
                    <div className={styles.heroCopy}>
                        <div className={styles.heroEyebrow}>{t.heroEyebrow}</div>
                        <h1 className={styles.heroTitle}>
                            {t.heroTitleTop}<br /><span className={styles.heroTitleAccent}>{t.heroTitleAccent}</span>
                        </h1>
                        <p className={styles.heroLead}>{t.heroLead}</p>
                    </div>
                    <div className={styles.heroBenefits}>
                        {t.benefits.map((benefit, index) =>
                            <div className={styles.benefit} key={index}><span className={styles.benefitMark}>✓</span>{benefit}</div>)}
                        <div className={styles.benefitFootnote}>{t.benefitFootnote}</div>
                    </div>
                </section>

                {this.renderCtaBar(t)}

                {this.state.phase === 'pending' && this.renderStatusBand(t)}

                <div className={styles.layout}>
                    <aside className={styles.aside}>
                        {SECTION_IDS.map((id, index) =>
                            <NavHashLink key={id} to={`#${id}`} scroll={smoothScroll} className={styles.asideLink}>{t.nav[index]}</NavHashLink>)}
                        <div className={styles.asideSupport}>
                            <div className={styles.asideSupportTitle}>{t.supportAsideTitle}</div>
                            <div className={styles.asideSupportPhone}>{SUPPORT_PHONE}</div>
                            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                        </div>
                    </aside>

                    <main className={styles.main}>
                        <section id={SECTION_IDS[0]} className={styles.card}>
                            <h2 className={styles.cardTitle}>{t.whatIsTitle}</h2>
                            <p className={styles.lead}>{t.whatIsBody}</p>
                            <div className={styles.tiles}>
                                {t.whatIsCards.map((card, index) =>
                                    <div className={styles.tile} key={index}>
                                        <div className={styles.tileMark}>
                                            <img className={styles.tileIcon} src={TILE_ICONS[index]} alt="" />
                                        </div>
                                        <div className={styles.tileTitle}>{card.title}</div>
                                        <div className={styles.tileBody}>{card.body}</div>
                                    </div>)}
                            </div>
                        </section>

                        <section id={SECTION_IDS[1]} className={styles.card}>
                            <h2 className={styles.cardTitle}>{t.stepsTitle}</h2>
                            <div className={styles.steps}>
                                {t.steps.map((step, index) =>
                                    <div className={styles.step} key={index}>
                                        <div className={styles.stepNumber}>{`0${index + 1}`}</div>
                                        <div className={styles.stepTitle}>{step.title}</div>
                                        <div className={styles.stepBody}>{step.body}</div>
                                    </div>)}
                            </div>
                            {this.state.phase === 'eligible' &&
                                <div className={styles.stepsActions}>
                                    <button className={styles.applyButton} onClick={this.openApplication}>{t.startApply}</button>
                                    <div className={styles.agreeNote}>
                                        {t.agreeNoteBefore}<a href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t.agreeNoteLink}</a>.
                                    </div>
                                </div>}
                            {this.state.phase === 'blocked' &&
                                <div className={styles.blockedNote}>
                                    {t.blockedBody} {SUPPORT_PHONE} · <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                                </div>}
                        </section>

                        <section id={SECTION_IDS[2]} className={styles.card}>
                            <h2 className={styles.cardTitle}>{t.termsTitle}</h2>
                            <div className={styles.termsTable}>
                                {t.termsRows.map((row, index) =>
                                    <div className={styles.termsRow} key={index}>
                                        <div className={styles.termsKey}>{row.k}</div>
                                        <div className={styles.termsValue}>{row.v}</div>
                                    </div>)}
                            </div>
                            <div className={styles.termsNote}>{t.termsNote}</div>
                        </section>

                        <section id={SECTION_IDS[3]} className={styles.moreBand}>
                            <div>
                                <h2 className={styles.moreTitle}>{t.moreTitle}</h2>
                                <div className={styles.moreBody}>{t.moreBody}</div>
                            </div>
                            <div className={styles.moreActions}>
                                <a className={styles.moreButton} href={FAQ_URL} target="_blank" rel="noopener noreferrer">{t.moreFaqButton} →</a>
                                <a className={styles.moreLink} href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t.moreTermsButton} →</a>
                            </div>
                        </section>

                        <section className={styles.supportBand}>
                            <div>
                                <div className={styles.supportBandTitle}>{t.supportBandTitle}</div>
                                <div className={styles.supportBandBody}>{t.supportBandBody}</div>
                            </div>
                            <a className={styles.supportBandButton} href={`mailto:${SUPPORT_EMAIL}`}>{t.supportBandButton}</a>
                        </section>
                    </main>
                </div>
            </div>
        );
    }
}

export default connect(
    state => ({
        translation: state.translation.idCreditApplication,
        login: state.login,
    }),
    dispatch => ({
        idCreditRequest: (body, success, error) => privateIdCreditApplicationRequest(dispatch, body, success, error),
        idCreditStatusChange: (hasCredit) => dispatch(idCreditStatusChange(hasCredit)),
    })
)(IdCreditApplicationPage);
